import React, { useState } from "react";
import { MdAutoAwesome, MdExpandLess, MdExpandMore } from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Button from "components/button";
import { SearchInput, Select } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState } from "components/states";
import { formatDate, formatNumber } from "utils/format";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "longest", label: "Longest" },
  { value: "shortest", label: "Shortest" },
];

const LONG_TEXT_LENGTH = 150;

const TextResponsesCard = ({
  responses = [],
  loading = false,
  stats = {},
  searchQuery = "",
  onSearchChange,
  sortOrder = "newest",
  onSortChange,
  onGenerateAISummary,
  isGeneratingSummary = false,
}) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedTheme, setSelectedTheme] = useState(null);

  const { count: totalCount = 0, aiSummary } = stats;

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderResponses = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-20 w-full rounded-card" />
          ))}
        </div>
      );
    }
    if (responses.length === 0) {
      return searchQuery ? (
        <Card padding="md">
          <EmptyState
            variant="filtered"
            title="No responses match your search"
            action={
              <Button variant="secondary" onClick={() => onSearchChange("")}>
                Clear Search
              </Button>
            }
          />
        </Card>
      ) : (
        <Card padding="md">
          <EmptyState variant="inline" title="No text responses yet" />
        </Card>
      );
    }
    return (
      <ul className="flex flex-col gap-3">
        {responses.map((response) => {
          const isExpanded = expandedIds.has(response.id);
          const needsExpand = response.answer && response.answer.length > LONG_TEXT_LENGTH;

          return (
            <Card as="li" key={response.id} padding="sm">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-fg">{response.respondentName}</span>
                <span className="shrink-0 text-xs text-fg-muted">
                  {response.submittedAt ? formatDate(response.submittedAt) : ""}
                </span>
              </div>
              <p
                className={`text-sm text-fg-muted ${
                  !isExpanded && needsExpand ? "line-clamp-2" : ""
                }`}
              >
                {response.answer}
              </p>
              {needsExpand && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 self-start"
                  aria-expanded={isExpanded}
                  leftIcon={
                    isExpanded ? (
                      <MdExpandLess aria-hidden="true" className="h-4 w-4" />
                    ) : (
                      <MdExpandMore aria-hidden="true" className="h-4 w-4" />
                    )
                  }
                  onClick={() => toggleExpand(response.id)}
                >
                  {isExpanded ? "Show Less" : "Show More"}
                </Button>
              )}
            </Card>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Card padding="md">
        <CardHeader
          title="AI Themes"
          titleAs="h4"
          actions={
            !aiSummary && (
              <Button
                size="sm"
                loading={isGeneratingSummary}
                disabled={totalCount === 0}
                leftIcon={<MdAutoAwesome aria-hidden="true" />}
                onClick={onGenerateAISummary}
              >
                {isGeneratingSummary ? "Generating…" : "Generate Insights"}
              </Button>
            )
          }
        />

        {aiSummary ? (
          <div className="mt-4 space-y-4">
            {aiSummary.themes && aiSummary.themes.length > 0 && (
              <div>
                <p className="mb-2 text-xs text-fg-muted">Select a theme to filter responses</p>
                <div className="flex flex-wrap gap-2">
                  {aiSummary.themes.map((theme, idx) => {
                    const isSelected = selectedTheme === theme.theme;
                    return (
                      <button
                        key={idx}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => setSelectedTheme(isSelected ? null : theme.theme)}
                        className={`focus-ring rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          isSelected
                            ? "bg-primary text-primary-fg"
                            : "bg-brand-100 text-brand-800 hover:bg-brand-200 dark:bg-brand-900/40 dark:text-brand-200 dark:hover:bg-brand-900/60"
                        }`}
                      >
                        {theme.theme} ({theme.percent}%)
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {aiSummary.sentiment && (
              <div>
                <p className="mb-2 text-xs font-medium text-fg-muted">Sentiment</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-success-fg">Positive: {aiSummary.sentiment.positive}%</span>
                  <span className="text-fg-muted">Neutral: {aiSummary.sentiment.neutral}%</span>
                  <span className="text-warning-fg">Concerns: {aiSummary.sentiment.concerns}%</span>
                </div>
              </div>
            )}

            {aiSummary.keyQuotes && aiSummary.keyQuotes.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-fg-muted">Key Quotes</p>
                <div className="space-y-2">
                  {aiSummary.keyQuotes.slice(0, 3).map((quote, idx) => (
                    <blockquote key={idx} className="text-sm italic text-fg-muted">
                      "{quote}"
                    </blockquote>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="mt-2 text-sm text-fg-muted">
            Generate AI insights to see common themes and sentiment analysis
          </p>
        )}
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={searchQuery}
          onChange={onSearchChange}
          placeholder="Search responses…"
          ariaLabel="Search responses"
          className="w-full sm:flex-1"
        />
        <Select
          aria-label="Sort responses"
          value={sortOrder}
          onChange={(e) => onSortChange(e.target.value)}
          options={SORT_OPTIONS}
          className="w-full sm:w-40"
        />
      </div>

      <p className="text-sm text-fg-muted" aria-live="polite">
        {selectedTheme && `Showing responses for "${selectedTheme}": `}
        {formatNumber(responses.length)} of {formatNumber(totalCount)} responses
      </p>

      {renderResponses()}
    </div>
  );
};

export default TextResponsesCard;
